import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';
import SchedulingPage from './SchedulingPage';
import ProductivityGraph from './ProductivityGraph';
import ManagerDashboard from './ManagerDashboard';
import { loadContent, ContentData } from '../utils/contentLoader';
import { 
  Shield, 
  Eye, 
  BarChart3, 
  Users, 
  CheckCircle, 
  TrendingUp, 
  Scale, 
  Phone, 
  Download,
  ChevronRight,
  Clock,
  FileText,
  AlertTriangle,
  X,
  Smartphone
} from 'lucide-react';

const HomePage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<'home' | 'scheduling'>('home');
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });

  // Load content from JSON files
  const content: ContentData = loadContent();

  // Helper function to get icon component by name
  const getIconComponent = (iconName: string) => {
    const iconMap: { [key: string]: any } = {
      Phone,
      BarChart3,
      Users,
      Eye,
      FileText,
      AlertTriangle,
      Shield,
      TrendingUp,
      Scale,
      Download,
      ChevronRight,
      Clock,
      X,
      Smartphone
    };
    return iconMap[iconName] || Phone; // fallback to Phone if icon not found
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleScheduleDemo = () => {
    setCurrentPage('scheduling');
  };

  const handleBackToHome = () => {
    setCurrentPage('home');
  };

  const handleCloseModal = () => {
    setShowDownloadModal(false);
  };

  if (currentPage === 'scheduling') {
    return <SchedulingPage onBack={handleBackToHome} />;
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Nix Spaces | Workplace Phone Monitoring for Employers"
        description="Monitor employee phone use during work hours. OSHA-ready logs, supervisor alerts, no content access. For warehouse, industrial & field operations."
        canonical="https://nixspaces.com"
        keywords="workplace phone monitoring, employer monitoring software, warehouse safety, industrial compliance, OSHA monitoring"
      />

      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <img src="/image.png" alt="Nix Logo" className="h-12 w-auto mt-1" />
              <span className="text-2xl font-bold text-gray-900">Spaces</span>
            </div>
            <nav className="hidden md:flex space-x-8">
              <a href="#features" className="text-gray-600 hover:text-blue-600 transition-colors">Features</a>
              <a href="#benefits" className="text-gray-600 hover:text-blue-600 transition-colors">Benefits</a>
              <Link to="/pricing" className="text-gray-600 hover:text-blue-600 transition-colors">Pricing</Link>
              <a href="#contact" className="text-gray-600 hover:text-blue-600 transition-colors">Contact</a>
            </nav>
            <button 
              onClick={handleScheduleDemo}
              className="bg-cyan-500 text-white px-4 py-2 rounded-2xl hover:bg-cyan-600 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Request Demo
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-gray-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              {content.hero.title}
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Monitor employee phone activity during work hours only. OSHA-ready logs, manager alerts, and compliance documentation for <Link to="/warehouse-phone-monitoring" className="text-cyan-600 hover:underline font-semibold">warehouse</Link> and <Link to="/industrial-safety-monitoring" className="text-cyan-600 hover:underline font-semibold">industrial operations</Link>.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => setShowDownloadModal(true)}
                className="bg-cyan-500 text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-cyan-600 transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl"
              >
                <Download className="h-5 w-5" />
                <span>{content.hero.primaryButton.text}</span>
              </button>
              <button 
                onClick={handleScheduleDemo}
                className="border-2 border-cyan-500 text-cyan-500 px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-cyan-500 hover:text-white transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                {content.hero.secondaryButton.text}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Simple, automated monitoring that keeps your team focused and compliant
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-cyan-100 w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Phone className="h-8 w-8 text-cyan-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Monitor Activity</h3>
              <p className="text-gray-600 leading-relaxed">
                Automatically track phone usage during work shifts with minimal impact on device performance
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-emerald-100 w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <BarChart3 className="h-8 w-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Generate Reports</h3>
              <p className="text-gray-600 leading-relaxed">
                Create detailed compliance logs and usage reports with timestamps and activity details
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-violet-100 w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Users className="h-8 w-8 text-violet-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Share with Managers</h3>
              <p className="text-gray-600 leading-relaxed">
                Real-time dashboard access for managers to monitor team compliance and safety
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Productivity Graph */}
      <ProductivityGraph />

      {/* Key Features */}
      <section id="features" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{content.features.title}</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {content.features.subtitle}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {content.features.features.map((feature: any, index: number) => {
              const IconComponent = getIconComponent(feature.icon);
              return (
                <div key={index} className="bg-white p-8 rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                  <div className={`bg-${feature.color}-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6`}>
                    <IconComponent className={`h-7 w-7 text-${feature.color}-600`} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Manager Dashboard Section */}
      <section id="manager-view" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Manager Dashboard</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Real-time insights into your team's phone usage and productivity
            </p>
          </div>
          
          <ManagerDashboard />
        </div>
      </section>

      {/* Benefits */}
      <section id="benefits" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{content.benefits.title}</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {content.benefits.subtitle}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {content.benefits.benefits.map((benefit: any, index: number) => {
              const IconComponent = getIconComponent(benefit.icon);
              return (
                <div key={index} className="bg-white p-8 rounded-3xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
                  <div className={`bg-${benefit.color}-100 w-20 h-20 rounded-3xl flex items-center justify-center mb-6 shadow-lg`}>
                    <IconComponent className={`h-8 w-8 text-${benefit.color}-600`} />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{benefit.title}</h3>
                  <ul className="space-y-2 text-gray-600">
                    {benefit.items.map((item: any, itemIndex: number) => (
                      <li key={itemIndex} className="flex items-start space-x-2">
                        <CheckCircle className={`h-5 w-5 text-${benefit.color}-500 mt-0.5 flex-shrink-0`} />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Policy Kit CTA */}
      <section className="py-16 bg-gradient-to-br from-cyan-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-cyan-100">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-cyan-500 mb-6">
              <FileText className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Free Resource: Shift Distraction Policy Kit
            </h2>
            <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
              Get leadership buy-in with a ready-to-adapt phone policy for warehouses, industrial sites, and field operations. Includes policy template, phased rollout plan, employee FAQ, and acknowledgment forms.
            </p>
            <a
              href="/shift-distraction-policy-kit"
              className="inline-flex items-center space-x-2 bg-cyan-500 text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-cyan-600 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              <FileText className="h-5 w-5" />
              <span>Download Free Policy Kit</span>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Get Started Today</h2>
            <p className="text-xl text-gray-600">
              Ready to improve workplace safety and productivity? Contact us for a personalized demo.
            </p>
          </div>
          
          <div className="bg-gray-50 rounded-2xl p-8 md:p-12">
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg border border-gray-100">
            <form 
              name="contact"
              method="POST"
              data-netlify="true"
              className="space-y-6"
            >
              <input type="hidden" name="form-name" value="contact" />
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-300"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Work Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-300"
                    placeholder="john@company.com"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-300"
                  placeholder="Your Company"
                />
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-300"
                  placeholder="Tell us about your team size and specific needs..."
                />
              </div>
              
              <button
                type="submit"
                className="w-full bg-cyan-500 text-white py-4 px-6 rounded-2xl text-lg font-semibold hover:bg-cyan-600 transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl"
              >
                <span>Request Demo</span>
                <ChevronRight className="h-5 w-5" />
              </button>
            </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div className="flex items-center space-x-3">
              <img src="/image.png" alt="Nix Logo" className="h-8 w-auto filter invert" />
              <span className="text-xl font-bold">Spaces</span>
            </div>
            
            {/* Footer Links */}
            <div className="flex flex-wrap items-center justify-center space-x-6 text-sm">
              <Link to="/warehouse-phone-monitoring" className="text-gray-400 hover:text-white transition-colors">
                Warehouse Safety
              </Link>
              <Link to="/industrial-safety-monitoring" className="text-gray-400 hover:text-white transition-colors">
                Industrial Safety
              </Link>
              <Link to="/pricing" className="text-gray-400 hover:text-white transition-colors">
                Pricing
              </Link>
              <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">
                Terms of Service
              </Link>
              <span className="text-gray-400">
                © 2024 Nix Spaces. All rights reserved.
              </span>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Download Modal */}
      {showDownloadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">Download Nix Spaces</h3>
                <button 
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <p className="text-gray-600 mb-6 text-center">
                Choose your platform to download the Nix Spaces app
              </p>
              
              <div className="space-y-4">
                {/* iOS Download */}
                <button 
                  onClick={() => window.open('https://apps.apple.com/us/app/nixspaces/id6738145824', '_blank')}
                  className="w-full flex items-center justify-between p-4 border-2 border-gray-200 rounded-2xl hover:border-cyan-500 hover:bg-cyan-50 transition-all duration-300"
                >
                  <div className="flex items-center space-x-4">
                    <div className="bg-gray-100 w-12 h-12 rounded-2xl flex items-center justify-center">
                      <Smartphone className="h-6 w-6 text-gray-600" />
                    </div>
                    <div className="text-left">
                      <div className="font-semibold text-gray-900">iOS App</div>
                      <div className="text-sm text-gray-500">Download from App Store</div>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </button>
                
                {/* Android Download */}
                <button 
                  onClick={() => window.open('https://play.google.com/store/apps/details?id=com.gopoint.nixspaces&pli=1', '_blank')}
                  className="w-full flex items-center justify-between p-4 border-2 border-gray-200 rounded-2xl hover:border-cyan-500 hover:bg-cyan-50 transition-all duration-300"
                >
                  <div className="flex items-center space-x-4">
                    <div className="bg-gray-100 w-12 h-12 rounded-2xl flex items-center justify-center">
                      <Smartphone className="h-6 w-6 text-gray-600" />
                    </div>
                    <div className="text-left">
                      <div className="font-semibold text-gray-900">Android App</div>
                      <div className="text-sm text-gray-500">Download from Google Play</div>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </button>
              </div>
              
              <div className="mt-6 p-4 bg-cyan-50 rounded-2xl border border-cyan-100">
                <div className="text-sm text-cyan-800">
                  <strong>Note:</strong> The app requires employer setup and configuration. Contact your IT administrator or request a demo to get started.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
